import type { FC } from 'react';

import ListingDetailPanel from '@/common-components/ListingDetailPanel';
import SideDrawer from '@/common-components/SideDrawer';
import type { UseMediaGalleryResult } from '@/hooks/useMediaGallery.hook';
import type { AiFlaggedItem } from '@/types/moderation.types';
import type { AdminListingDetail } from '@/types/rental.types';

interface AiListingDetailDrawerProps {
  item: AiFlaggedItem | null;
  detail: AdminListingDetail | undefined;
  gallery: UseMediaGalleryResult;
  isLoadingDetail: boolean;
  isErrorDetail: boolean;
  mutatingId: string | null;
  onClose: () => void;
  onDismiss: (item: AiFlaggedItem) => void;
  onRemove: (item: AiFlaggedItem) => void;
}

const footerBtn =
  'w-full rounded-lg border py-2.5 text-[13px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50';

/**
 * A listing opened from the AI Flagged queue.
 *
 * Same body as Rental Management's drawer, different footer: approving here would leave the AI
 * flag set and the row sitting in the queue, so the actions are the queue's own dismiss/remove.
 */
const AiListingDetailDrawer: FC<AiListingDetailDrawerProps> = ({
  item,
  detail,
  gallery,
  isLoadingDetail,
  isErrorDetail,
  mutatingId,
  onClose,
  onDismiss,
  onRemove,
}) => {
  const isMutating = !!(item && mutatingId === item.id);

  const footer = item ? (
    <div className="flex gap-2">
      <button
        type="button"
        disabled={isMutating}
        onClick={() => onDismiss(item)}
        className={`${footerBtn} border-[var(--color-divider)] bg-white text-[var(--color-ink-muted)] hover:bg-[var(--color-surface-muted)]`}
      >
        {isMutating ? 'Working…' : 'Dismiss Flag'}
      </button>
      <button
        type="button"
        disabled={isMutating}
        onClick={() => onRemove(item)}
        className={`${footerBtn} border-[var(--color-danger)] bg-[var(--color-danger-bg)] text-[var(--color-danger-ink)] hover:bg-[var(--color-danger)] hover:text-white`}
      >
        {isMutating ? 'Working…' : 'Remove Listing'}
      </button>
    </div>
  ) : undefined;

  const banner = item ? (
    <div className="border-b border-[var(--color-divider)] bg-[var(--color-pink-bg)] px-5 py-3">
      <p className="text-[10.5px] font-bold uppercase tracking-wider text-[var(--color-pink-ink)]">
        🤖 AI flag
      </p>
      <p className="mt-1 text-[12.5px] leading-relaxed text-[var(--color-ink)]">
        {item.flagReason}
      </p>
    </div>
  ) : null;

  return (
    <SideDrawer
      isOpen={item !== null}
      title="Flagged Listing"
      onClose={onClose}
      footer={footer}
    >
      <ListingDetailPanel
        title={detail?.title ?? item?.title ?? ''}
        category={detail?.category}
        campusName={item?.campusName}
        detail={detail}
        gallery={gallery}
        isLoading={isLoadingDetail}
        isError={isErrorDetail}
        banner={banner}
      />
    </SideDrawer>
  );
};

export default AiListingDetailDrawer;
